import React from 'react';
import {z} from 'zod';
import {zodResolver} from '@hookform/resolvers/zod';
import {Controller, useForm} from 'react-hook-form';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import {LoadingButton} from '@mui/lab';
import {Prompt} from 'app/types/common';
import {isNil} from 'app/util/isNil';

interface DialogProps extends Prompt {
  onCreate(name: string): Promise<void>;
}

export function CreateGroupDialog({open, onCreate, onClose}: DialogProps) {
  return (
    <Dialog fullWidth={true} maxWidth="xs" open={open} onClose={onClose}>
      <CreateGroup onCreate={onCreate} onCancel={onClose} />
    </Dialog>
  );
}

const scheme = z.object({name: z.string().trim().min(1, {message: 'Required field'})});

export type FormFields = z.infer<typeof scheme>;

interface Props {
  onCreate(name: string): Promise<void>;
  onCancel(): void;
}

function CreateGroup({onCreate, onCancel}: Props) {
  const {
    control,
    formState: {isSubmitting},
    handleSubmit,
  } = useForm<FormFields>({
    defaultValues: {name: ''},
    resolver: zodResolver(scheme),
  });

  const handleCreate = async (fields: FormFields) => {
    await onCreate(fields.name);
  };

  return (
    <Box component="form" onSubmit={handleSubmit(handleCreate)}>
      <DialogTitle>New group</DialogTitle>

      <DialogContent>
        <Controller
          control={control}
          name="name"
          render={({field, fieldState: {error}}) => (
            <TextField
              placeholder="Group name"
              fullWidth={true}
              variant="standard"
              autoFocus={true}
              error={!isNil(error)}
              helperText={error?.message ?? ' '}
              disabled={isSubmitting}
              {...field}
            />
          )}
        />
      </DialogContent>

      <DialogActions>
        <LoadingButton
          data-id="confirm-create-button"
          variant="contained"
          color="secondary"
          type="submit"
          loading={isSubmitting}
        >
          Create
        </LoadingButton>

        <Button variant="outlined" color="info" onClick={onCancel}>
          Cancel
        </Button>
      </DialogActions>
    </Box>
  );
}
